// Pages (TOM-618, a local change: see VENDORED.md): the browser half. A page
// is drawn in its card in a sealed frame. The host makes the frame with
// createPageFrame, so the web and the iPhone seal it the same way; drawPage
// fills it and keeps its live values current. Page content never goes into
// the frame's srcdoc: it is parsed apart, walked against page-rules.js and
// only then inserted through the DOM.
import { normalizeData } from "./data.js";
import { renderControl } from "./render.js";
import { ControlMotion, patchControlSVG } from "./animation.js";
import { applyFit, faceFits, measureFace, stableJSON } from "./fit.js";
import { numberReadout } from "./render-instruments.js";
import { themes, themeFor } from "./themes.js";
import { marinaKinds, marinaSeries } from "./marina-theme.generated.js";
import {
  PAGE_CSP,
  PAGE_SANDBOX,
  PAGE_MAX_FACES,
  PAGE_MAX_ELEMENTS,
  PAGE_MAX_NODES,
  PAGE_MAX_USE_DEPTH,
  elementAllowed,
  elementKeptWhole,
  attributeAllowed,
  cssLooksSafe,
  pageCssV2Refusals,
  pageWithinLimits,
  pageVariables,
  pageValueText,
  pageAgeText,
} from "./page-rules.js";
import { fillPageBindings, pageBindingIndex } from "./page-bindings.js";

/**
 * A CSS filter that turns a page red for night vision: white reads as the
 * night theme's own text (#ef9a89, themes.js), yellow a little dimmer, black
 * stays black. The host puts it on the frame in the night theme unless the
 * page says `night: "keep"`. Each step stays inside 0 to 1 for every colour,
 * so no browser's clamping between steps changes it (TOM-622).
 */
export const NIGHT_TINT =
  "grayscale(1) brightness(0.34) sepia(1) saturate(1.75) hue-rotate(-38deg) brightness(1.62)";

const themeNames = ["light", "dark", "night"],
  sizes = ["s", "m", "l"];
// Each theme's tokens (themes.js), switched by the drawer's data-theme.
const themeTokens = themeNames
  .map((theme) => {
    const tokens = { ...themes[theme], accent: themeFor({ theme }).accent };
    return `html[data-theme="${theme}"]{${Object.entries(tokens)
      .map(([key, value]) => `--${key}:${value}`)
      .join(";")}}`;
  })
  .join("");
// The board's own type (cloud/web tokens.css), as the web gives its panels
// (VENDORED.md, local change 1): the frame inherits nothing from its host.
const fonts = `--tc-font-sans:-apple-system,BlinkMacSystemFont,"SF Pro Text","Helvetica Neue",system-ui,sans-serif;--tc-font-mono:ui-monospace,"SF Mono",SFMono-Regular,Menlo,Consolas,monospace`;
// A face slot with no height of its own is as tall as its face's width allows,
// never taller than the frame (100vh is the frame's height, TOM-620): a page
// that is one face shows it whole, centred, in a card of any size.
// A face's own foot line, its source and age and its quality word ("STALE",
// "DISCONNECTED"), is hidden as a board panel hides it: the host's card says
// how old the reading is, outside the page (TOM-622). The rule weighs
// nothing (:where), so any rule of the page's own shows them again.
// A version 2 page's tokens (TOM-665): the chart's series colours per theme,
// as cloud/web tokens.css draws its series (the chart line, --text-1, --text-3,
// and the forecast's --text-3), and the board's rounded numerals. A page reads
// them and never sets them.
const seriesTokens = {
  light: marinaSeries.light,
  dark: marinaSeries.dark,
  night: ["#d95a45", "#d95a45", "#b23f2a", "#b23f2a"],
};
const seriesRules = themeNames
  .map(
    (theme) =>
      `html[data-theme="${theme}"]{${seriesTokens[theme]
        .map((colour, i) => `--tc-series-${i + 1}:${colour}`)
        .join(";")}}`,
  )
  .join("");
// Each kind of reading's colour (cloud ADR 0396, TOM-676): --tc-<kind> is its
// line, then -fill, -fill-top and -fill-bottom for the area under it and -icon
// for its symbol, per theme, from the Marina tokens. Night is one red. A page
// reads them and never sets them.
const kindRules = themeNames
  .map(
    (theme) =>
      `html[data-theme="${theme}"]{${Object.entries(marinaKinds[theme])
        .map(
          ([kind, k]) =>
            `--tc-${kind}:${k.line};--tc-${kind}-fill:${k.fill};--tc-${kind}-fill-top:${k.fillTop};--tc-${kind}-fill-bottom:${k.fillBottom};--tc-${kind}-icon:${k.icon}`,
        )
        .join(";")}}`,
  )
  .join("");
const rounded = `--tc-font-rounded:ui-rounded,"SF Pro Rounded",-apple-system,BlinkMacSystemFont,system-ui,sans-serif`;
// The content height is measured with html and body at their own height
// (TOM-665): a page that fills its frame never grows its own card.
const base = `html,body{margin:0;height:100%;overflow:hidden;background:transparent}html{${fonts};${rounded};font-family:var(--tc-font-sans);color:var(--text);font-variant-numeric:tabular-nums}${themeTokens}${seriesRules}${kindRules}tc-face,tc-history{display:block}body>:is(tc-face,tc-history):only-child{height:100%}tc-face>svg,tc-history>svg{display:block;width:100%;height:100%;max-height:100vh}:where(tc-face,tc-history) :where([data-part="footer"],[data-part="status"]){display:none}tc-live{display:block;position:relative;box-sizing:border-box}html.tc-measure,html.tc-measure body{height:auto!important}`;

/**
 * The frame's whole document, for its srcdoc: the policy first in head, the
 * theme's tokens and the fonts, an empty style for the page's CSS, an empty
 * style for the readings' values and an empty body. drawPage puts the page
 * in.
 */
export function pageDocument({ theme = "dark", size = "m" } = {}) {
  const t = themeNames.includes(theme) ? theme : "dark",
    s = sizes.includes(size) ? size : "m";
  return `<!doctype html><html data-theme="${t}" data-size="${s}"><head><meta http-equiv="Content-Security-Policy" content="${PAGE_CSP}"><meta charset="utf-8"><meta name="referrer" content="no-referrer"><style id="tc-base">${base}</style><style id="tc-page"></style><style id="tc-values"></style></head><body></body></html>`;
}

/**
 * A frame for a page, sealed: the sandbox lets the host read and fill it and
 * runs nothing inside. Its colour scheme is fixed so a host in dark mode still
 * sees through it: a frame whose scheme differs from its document's is drawn
 * opaque.
 */
export function createPageFrame(document, { theme, size } = {}) {
  const frame = document.createElement("iframe");
  frame.setAttribute("sandbox", PAGE_SANDBOX);
  frame.setAttribute("referrerpolicy", "no-referrer");
  frame.setAttribute("srcdoc", pageDocument({ theme, size }));
  frame.style.setProperty("color-scheme", "normal");
  return frame;
}

const frames = new WeakMap();

/**
 * Draw a page in a frame made by createPageFrame, and keep it current: call
 * it again with each new PageData. The first call waits for the frame to
 * load; a call with different html or css puts the page in again. It
 * resolves to the page's live areas and content height after the paint
 * (TOM-665); `options` pauses motion and reports either when it changes.
 * `options.results` draws a chat page (cloud ADR 0399, TOM-870): its
 * `data-tc-result` bindings are kept and filled from `data.results`; on any
 * other page the walk drops them.
 */
export function drawPage(frame, page, data, options) {
  let state = frames.get(frame);
  if (!state) {
    state = { ready: loaded(frame), slots: [], set: new Set() };
    frames.set(frame, state);
  }
  return state.ready.then(() =>
    paint(frame, state, page ?? {}, data ?? {}, options ?? {}),
  );
}

/**
 * The text a screen reader should hear for the page now: its visible text,
 * values included, in document order (TOM-665).
 */
export function pageAccessibleText(frame) {
  const doc = frame?.contentDocument;
  const body = doc?.body;
  if (!body) return "";
  const view = doc.defaultView;
  const out = [];
  const walk = (node) => {
    for (const child of node.childNodes ?? []) {
      if (child.nodeType === 3) {
        const text = String(child.textContent ?? "").trim();
        if (text) out.push(text);
        continue;
      }
      if (child.nodeType !== 1) continue;
      const style = view?.getComputedStyle?.(child);
      if (style && (style.display === "none" || style.visibility === "hidden"))
        continue;
      if (child.getAttribute?.("aria-hidden") === "true") continue;
      walk(child);
    }
  };
  walk(body);
  return out.join(" ").replace(/\s+/g, " ").trim();
}

function ours(frame) {
  return frame.contentDocument?.head?.querySelector("style#tc-page") ?? null;
}

function loaded(frame) {
  if (ours(frame)) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      if (!ours(frame)) return;
      frame.removeEventListener("load", done);
      resolve();
    };
    frame.addEventListener("load", done);
  });
}

function paint(frame, state, page, data, options) {
  const doc = frame.contentDocument,
    style = ours(frame);
  if (!doc || !style) return { liveAreas: [], contentHeight: 0 };
  if (doc !== state.doc) {
    release(state);
    state.observer?.disconnect();
    Object.assign(state, {
      doc,
      style,
      html: null,
      css: null,
      v2: null,
      set: new Set(),
      results: null,
      live: null,
      height: null,
      written: new WeakMap(),
      // A new document's svgs have not been paused yet (TOM-665).
      reduced: undefined,
      observer: null,
      dirty: true,
    });
  }
  const html = typeof page.html === "string" ? page.html : "",
    css = typeof page.css === "string" ? page.css : "",
    v2 = page.v === 2,
    results = options.results === true;
  if (
    html !== state.html ||
    css !== state.css ||
    v2 !== state.v2 ||
    results !== state.results
  ) {
    insert(frame, state, html, css, v2, page, results);
    state.html = html;
    state.css = css;
    state.v2 = v2;
    state.results = results;
  }
  update(frame, state, page, data);
  motion(state, options.reducedMotion === true);
  state.options = options;
  return measure(frame, state, options);
}

/** Pause CSS and SVG motion while the viewer asks for reduced motion, and let it run again after. */
function motion(state, reduced) {
  const doc = state.doc;
  let style = doc.head.querySelector?.("style#tc-motion") ?? null;
  if (reduced && !style) {
    style = doc.createElement("style");
    style.id = "tc-motion";
    style.textContent =
      "*,*::before,*::after{animation-play-state:paused !important;transition-duration:0s !important;transition-delay:0s !important}";
    doc.head.append(style);
  } else if (!reduced && style) {
    style.remove();
  }
  if (reduced !== state.reduced) {
    for (const svg of outermostSvgs(doc.body)) {
      try {
        if (reduced) svg.pauseAnimations?.();
        else svg.unpauseAnimations?.();
      } catch {
        // An svg with no animation timeline: nothing to pause.
      }
    }
    state.reduced = reduced;
  }
}

function outermostSvgs(node, out = []) {
  for (const child of node?.childNodes ?? []) {
    if (child.nodeType !== 1) continue;
    if (String(child.localName).toLowerCase() === "svg") out.push(child);
    else outermostSvgs(child, out);
  }
  return out;
}

/**
 * The live areas' boxes and the content height after the paint, reported to
 * the host when either moved (a live area by half a pixel or more).
 *
 * Measuring forces two layouts, so it is done only when something may have
 * moved them (TOM-665): after the page is put in, after a fitted drawing
 * changed size, or when the frame's ResizeObserver saw the page or one of its
 * parts change size. Otherwise the last measure stands. Without a
 * ResizeObserver every paint measures, as before.
 */
function measure(frame, state, options) {
  if (!state.dirty && state.observer && state.live !== null)
    return { liveAreas: state.live, contentHeight: state.height ?? 0 };
  state.dirty = false;
  const doc = state.doc;
  const liveAreas = [];
  for (const area of state.index?.live ?? []) {
    const box = area.getBoundingClientRect?.();
    if (!box) continue;
    liveAreas.push({
      id: area.getAttribute("id") ?? "",
      x: box.left,
      y: box.top,
      width: box.width,
      height: box.height,
    });
  }
  let contentHeight = 0;
  const root = doc.documentElement;
  root.classList?.add("tc-measure");
  try {
    const walk = (node) => {
      for (const child of node.childNodes ?? []) {
        if (child.nodeType !== 1) continue;
        const box = child.getBoundingClientRect?.();
        if (box && box.bottom > contentHeight) contentHeight = box.bottom;
        // An SVG that hides what it draws past its own box is measured by that
        // box (TOM-912): a number's reels hold the digits above and below the
        // one shown, and on a face fitted to its value they reach far below it.
        if (
          String(child.localName ?? child.tagName).toLowerCase() === "svg" &&
          (doc.defaultView?.getComputedStyle?.(child)?.overflow ??
            "visible") !== "visible"
        )
          continue;
        walk(child);
      }
    };
    walk(doc.body);
  } finally {
    root.classList?.remove("tc-measure");
  }
  contentHeight = Math.ceil(contentHeight);
  const moved =
    state.live === null ||
    state.live.length !== liveAreas.length ||
    liveAreas.some((a, i) => {
      const b = state.live[i];
      return (
        a.id !== b.id ||
        Math.abs(a.x - b.x) >= 0.5 ||
        Math.abs(a.y - b.y) >= 0.5 ||
        Math.abs(a.width - b.width) >= 0.5 ||
        Math.abs(a.height - b.height) >= 0.5
      );
    });
  if (moved) {
    state.live = liveAreas;
    if (liveAreas.length > 0 || state.reportedLive)
      options.onLiveAreas?.(liveAreas);
    state.reportedLive = liveAreas.length > 0;
  }
  if (contentHeight !== state.height) {
    state.height = contentHeight;
    options.onContentSize?.({
      width: doc.body.getBoundingClientRect?.().width ?? 0,
      height: contentHeight,
    });
  }
  return { liveAreas: state.live, contentHeight };
}

function release(state) {
  for (const slot of state.slots) slot.motion?.dispose();
  state.slots = [];
}

/**
 * Keep only what page-rules.js allows; drop the rest, subtree and all. An
 * animation element that holds a refused attribute goes whole, never kept
 * without it (TOM-587). A version 2 page (`options.version` 2, TOM-665) is
 * walked against its own rules: no picture, no host-set attribute, every
 * binding's value checked, and a `<tc-live>` held empty.
 */
function clean(node, options = {}) {
  for (const child of [...node.childNodes]) {
    if (child.nodeType === 3) continue;
    if (
      child.nodeType !== 1 ||
      !elementAllowed(child.localName, options) ||
      !elementKeptWhole(child.localName, [...child.attributes], options)
    ) {
      child.remove();
      continue;
    }
    for (const attribute of [...child.attributes])
      if (
        !attributeAllowed(
          child.localName,
          attribute.name,
          attribute.value,
          options,
        )
      )
        child.removeAttribute(attribute.name);
    if (
      options.version === 2 &&
      String(child.localName).toLowerCase() === "tc-live"
    ) {
      child.replaceChildren();
      continue;
    }
    clean(child, options);
  }
}

/**
 * How many elements a parse holds, every `<use>` counted as its target's
 * expanded size (a use of a use, to PAGE_MAX_USE_DEPTH), worked out once per
 * target: the drawing cost the drawer refuses past (TOM-665).
 */
function expandedCount(root) {
  const byId = new Map();
  const all = [];
  const collect = (node) => {
    for (const child of node.childNodes ?? []) {
      if (child.nodeType !== 1) continue;
      all.push(child);
      const id = child.getAttribute?.("id");
      if (id && !byId.has(id)) byId.set(id, child);
      collect(child);
    }
  };
  collect(root);
  const memo = new Map();
  const size = (el, depth) => {
    if (depth > PAGE_MAX_USE_DEPTH + 1) return Infinity;
    if (memo.has(el)) return memo.get(el);
    memo.set(el, Infinity); // a cycle is past every cap
    let n = 1;
    for (const child of el.childNodes ?? [])
      if (child.nodeType === 1) n += size(child, depth);
    if (String(el.localName).toLowerCase() === "use") {
      const href = el.getAttribute("href") ?? el.getAttribute("xlink:href");
      const target = href?.startsWith("#") ? byId.get(href.slice(1)) : null;
      if (target) n += size(target, depth + 1);
    }
    memo.set(el, n);
    return n;
  };
  let nodes = 0;
  for (const child of root.childNodes ?? [])
    if (child.nodeType === 1) nodes += size(child, 0);
  return { elements: all.length, nodes };
}

function insert(frame, state, html, css, v2, page, results = false) {
  release(state);
  const doc = state.doc,
    fits = pageWithinLimits({ html, css }),
    readingNames = Object.keys(page?.readings ?? {}),
    // A chat page keeps its result bindings (cloud ADR 0399, TOM-870).
    options = v2
      ? { version: 2, readingNames, ...(results ? { results: true } : {}) }
      : {};
  const parser = new frame.ownerDocument.defaultView.DOMParser();
  let parsed = parser.parseFromString(fits ? html : "", "text/html");
  let drawn = fits;
  if (v2 && fits) {
    // A page past the drawing cost caps is not drawn at all (§9.3).
    const count = expandedCount(parsed.body);
    if (count.elements > PAGE_MAX_ELEMENTS || count.nodes > PAGE_MAX_NODES) {
      parsed = parser.parseFromString("", "text/html");
      drawn = false;
    }
  }
  clean(parsed.body, options);
  for (const extra of [
    ...parsed.body.querySelectorAll("tc-face, tc-history"),
  ].slice(PAGE_MAX_FACES))
    extra.remove();
  const content = doc.createDocumentFragment();
  for (const node of [...parsed.body.childNodes])
    content.append(doc.importNode(node, true));
  doc.body.replaceChildren(content);
  const cssAllowed =
    drawn &&
    cssLooksSafe(css) &&
    (!v2 || pageCssV2Refusals(css, readingNames).length === 0);
  state.style.textContent = cssAllowed ? css : "";
  state.index = v2 ? pageBindingIndex(doc.body) : null;
  state.written = new WeakMap();
  // The new svgs are paused or not by the next motion() (TOM-665), and the
  // new page is measured on this paint and watched for size changes.
  state.reduced = undefined;
  state.dirty = true;
  observe(frame, state);
}

/**
 * Watch the page for size changes with the host's ResizeObserver: the root
 * (the frame's own size), each top-level part (a text that wraps grows the
 * part that holds it) and each live area. A change is measured at once and
 * reported to the host (TOM-665).
 */
function observe(frame, state) {
  const Observer = frame.ownerDocument?.defaultView?.ResizeObserver;
  if (typeof Observer !== "function") return;
  if (!state.observer)
    state.observer = new Observer(() => {
      if (state.doc !== frame.contentDocument) return;
      state.dirty = true;
      if (state.options) measure(frame, state, state.options);
    });
  const observer = state.observer;
  observer.disconnect();
  const doc = state.doc;
  observer.observe(doc.documentElement);
  for (const child of doc.body.childNodes ?? [])
    if (child.nodeType === 1) observer.observe(child);
  for (const area of state.index?.live ?? []) observer.observe(area);
}

function setText(element, text) {
  if (element.textContent !== text) element.textContent = text;
}

/**
 * The style the readings' values are written into: the frame's own, after
 * the page's, made here for a frame whose document lacks it.
 */
function valuesStyle(doc) {
  let style = doc.head.querySelector("style#tc-values");
  if (!style) {
    style = doc.createElement("style");
    style.id = "tc-values";
    doc.head.append(style);
  }
  return style;
}

function update(frame, state, page, data) {
  const doc = state.doc,
    root = doc.documentElement,
    { properties, attributes } = pageVariables(page, data),
    set = new Set();
  // The readings' values (`--name`, `--name-fraction`) go in a rule of their
  // own, never in the inline style of the frame's <html> (TOM-587). WebKit
  // keeps memory each time a custom property changes in an element's inline
  // style, more the more the page draws: on the iOS 27 simulator, 12 pages
  // updated once a second grew the board's web content process from 52 to
  // 189 MB in 2.5 minutes without end (TOM-623). The same values written as
  // a rule keep nothing. `html:root:root` outweighs a page's own `html` or
  // `:root`, as the inline style did; a value gone from this data is simply
  // not written.
  const values = Object.entries(properties)
    .filter(([, value]) => value !== null)
    .map(([name, value]) => `${name}:${value}`)
    .join(";");
  let rule = values ? `html:root:root{${values}}` : "";
  // A version 2 page's bindings (TOM-665): every text, state, path and
  // fraction the host fills, its fractions and points written as rules here
  // too, never in an inline style.
  if (state.index) {
    const boxes = state.index.fits.map((svg) => svg.getAttribute("viewBox"));
    rule += fillPageBindings(state.index, page, data, {
      written: state.written,
      window: frame.contentDocument?.defaultView,
      // A row shown or hidden inside a part that fills the frame changes no
      // observed size: this paint measures (TOM-665).
      onShape: () => {
        state.dirty = true;
      },
    });
    // A fitted drawing that changed size may have moved the page (TOM-665).
    if (
      state.index.fits.some(
        (svg, i) => svg.getAttribute("viewBox") !== boxes[i],
      )
    )
      state.dirty = true;
  }
  const style = valuesStyle(doc);
  if (style.textContent !== rule) style.textContent = rule;
  // A root attribute that changes (a zone, a quality, the theme, the slot)
  // can lay the page out again: this paint measures (TOM-665).
  for (const [name, value] of Object.entries(attributes))
    if (value === null) {
      if (root.hasAttribute(name)) {
        root.removeAttribute(name);
        state.dirty = true;
      }
    } else {
      set.add(name);
      if (root.getAttribute(name) !== value) {
        root.setAttribute(name, value);
        state.dirty = true;
      }
    }
  // A reading gone from this data takes its attributes with it.
  for (const name of state.set)
    if (!set.has(name) && root.hasAttribute(name)) {
      root.removeAttribute(name);
      state.dirty = true;
    }
  state.set = set;

  const readings = data.readings ?? {},
    reading = (element) => {
      const name = element.getAttribute("reading");
      return name && Object.hasOwn(readings, name) ? readings[name] : undefined;
    };
  for (const element of doc.body.querySelectorAll("tc-value"))
    setText(
      element,
      pageValueText(reading(element), element.getAttribute("decimals")),
    );
  for (const element of doc.body.querySelectorAll("tc-unit"))
    setText(element, String(reading(element)?.unit ?? ""));
  for (const element of doc.body.querySelectorAll("tc-age"))
    setText(element, pageAgeText(reading(element)?.measuredAt, data.now));

  // Each face slot, in document order, takes the face the host built for it.
  [...doc.body.querySelectorAll("tc-face, tc-history")].forEach(
    (element, index) => {
      let slot = state.slots[index];
      if (slot?.element !== element) {
        // A slot holds its face alone.
        slot?.motion?.dispose();
        slot = state.slots[index] = { element };
        element.replaceChildren();
      }
      const face = data.faces?.[index];
      if (!face?.instance) {
        slot.motion?.dispose();
        slot.motion = null;
        element.replaceChildren();
        return;
      }
      // The instance is compared by its content, never by the order of its
      // keys (fit.js, stableJSON): the iPhone sends a new copy each time.
      const key = stableJSON([
        face.instance.id,
        face.instance.definitionId,
        face.instance.bindings,
      ]);
      if (!slot.motion || slot.key !== key) {
        slot.motion?.dispose();
        slot.key = key;
        slot.box = null;
        // The library's own motion, as its element uses it (web.js): values
        // ease between updates, and patchControlSVG keeps the face's nodes,
        // so a page's CSS transitions carry on through every frame.
        slot.motion = new ControlMotion({
          paint: (values) => paintFace(slot, values, state),
          active: () => frame.isConnected && !frame.ownerDocument.hidden,
        });
      }
      // Options can move the face's parts: fit it again (fit.js). A face
      // whose options changed is drawn settled at once, so it is measured on
      // that paint: easing to the update first would draw it on the whole
      // 400 × 300 face, about two thirds as wide, until its motion ends.
      const look = stableJSON(face.instance.options ?? {});
      const restyled = slot.look !== look;
      if (restyled) {
        slot.look = look;
        slot.box = null;
      }
      slot.instance = face.instance;
      slot.kindAccent = !lookNamesAccent(element);
      try {
        const options = face.instance.options ?? {};
        slot.motion.update(normalizeData(face.instance, face.data ?? {}), {
          duration: options.animationDuration,
          // A number's readout shows each reading at once (TOM-956).
          enabled:
            options.animation !== "off" &&
            !restyled &&
            !numberReadout(face.instance.definitionId, options),
        });
      } catch {
        element.replaceChildren();
      }
    },
  );
}

/**
 * Whether a face's look names its accent (cloud ADR 0396, TOM-678): a face
 * whose look names none takes its reading's kind's colour (render.js).
 */
function lookNamesAccent(element) {
  try {
    const look = JSON.parse(element.getAttribute("look") ?? "{}");
    return (
      look !== null && typeof look === "object" && Object.hasOwn(look, "accent")
    );
  } catch {
    return false;
  }
}

function paintFace(slot, values, state) {
  try {
    const rendered = renderControl(slot.instance, values, {
      motionPhase: slot.motion.phase,
      kindAccent: slot.kindAccent === true,
    });
    const svg = patchControlSVG(slot.element, rendered.svg);
    // A dial or bar gauge fills its slot, as on a board's panel (TOM-587): a
    // panel turned into a page keeps the size it had.
    if (!svg || !faceFits(rendered)) {
      slot.box = null;
      return;
    }
    const before = slot.box;
    slot.box = measureFace(svg, rendered, slot.motion.phase, slot.box);
    if (slot.box) applyFit(svg, slot.box);
    // A face fitted to a new box may have changed the page's height (TOM-665).
    if (stableJSON(slot.box) !== stableJSON(before)) state.dirty = true;
  } catch {
    slot.element.replaceChildren();
  }
}
